"use client";

import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import Image from "next/image";
import Link from "next/link";
import { BlocksRenderer, BlocksContent } from "@strapi/blocks-react-renderer";
import { useEffect, useRef, useState } from "react";
import { absoluteCmsUrl } from "../base-image/BaseImage";
import { Heading, HeadingProps } from "@repo/ui/heading";

/* =========================
   Types for Strapi v5 (flat)
   ========================= */
type StrapiImageFormat = {
  url?: string | null;
  width?: number;
  height?: number;
  ext?: string;
  mime?: string;
  name?: string;
  size?: number;
};

type StrapiMedia = {
  id: number;
  url?: string | null;
  width?: number;
  height?: number;
  name?: string;
  alternativeText?: string | null;
  caption?: string | null;
  formats?: {
    large?: StrapiImageFormat;
    medium?: StrapiImageFormat;
    small?: StrapiImageFormat;
    thumbnail?: StrapiImageFormat;
  };
};

type SliderRoom = {
  id: number;
  title: string;
  slug: string;
  shortDescription?: string | null;
  description?: BlocksContent | null;
  image?: StrapiMedia | null;
  hotel?: { title?: string | null } | null;
};

interface Headline {
  heading: HeadingProps;
  description?: BlocksContent | null;
}

export interface SliderRoomsProps {
  headline: Headline;
  rooms: SliderRoom[];
  roomHrefBase?: string; // defaults to /rooms
}

type LogoConfig = {
  src: string;
  width: number;
  height: number;
  sizes?: string;
  className?: string;
};

const HOTEL_LOGOS: Record<string, LogoConfig> = {
  dottingen: {
    src: "/assets/dottingen-white-logo.svg",
    width: 152,
    height: 21,
    sizes: "(max-width:768px) 120px, (max-width:1024px) 160px, 180px",
  },
  littau: {
    src: "/assets/littau-logo.svg",
    width: 87,
    height: 22,
    sizes: "(max-width:768px) 110px, (max-width:1024px) 130px, 140px",
    className: "invert",
  },
};

const normalizeLocation = (v?: string | null) => {
  if (!v) return "";
  return v
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
};

const getHotelLogo = (location?: string | null): LogoConfig | null => {
  const key = normalizeLocation(location);
  return HOTEL_LOGOS[key] ?? null;
};

const toId = (title?: string | null) => {
  if (!title) return undefined;
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

const isNonEmpty = (v?: string | null): v is string =>
  typeof v === "string" && v.trim().length > 0;

function pickBestImageUrl(img?: StrapiMedia | null): string | null {
  if (!img) return null;
  const candidate =
    img.url ??
    img.formats?.large?.url ??
    img.formats?.medium?.url ??
    img.formats?.small?.url ??
    null;
  if (!isNonEmpty(candidate)) return null;
  const abs = absoluteCmsUrl(candidate);
  return isNonEmpty(abs) ? abs : null;
}

/* =========================
   Component
   ========================= */
export default function CardSliderRooms({
  headline,
  rooms,
  roomHrefBase = "/rooms",
}: SliderRoomsProps) {
  // Use rooms directly — no memoization
  const slides = Array.isArray(rooms) ? rooms : [];

  // Keen slider setup
  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    slides: { perView: 1.2, spacing: 16 },
    breakpoints: {
      "(min-width: 768px)": { slides: { perView: 2.2, spacing: 24 } },
      "(min-width: 1024px)": { slides: { perView: 2.8, spacing: 24 } },
      "(min-width: 1280px)": { slides: { perView: 3, spacing: 24 } },
    },
  });

  useEffect(() => {
    slider.current?.update?.();
  }, [slides.length, slider]);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    if (!rootRef.current || isVisible) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries && entries.length > 0 ? entries[0] : undefined;
        if (entry && entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: "200px 0px", threshold: 0.01 },
    );
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  const sectionId = toId(headline.heading.title);

  if (slides.length === 0) return null;

  return (
    <>
      <div
        className="max-w-7xl mx-auto relative reveal-stagger md:pb-14 md:pt-32 pb-6 pt-14 flex flex-col md:gap-10 gap-4 w-full text-center items-center px-6"
        aria-labelledby="house-rules-title"
        id={sectionId}
      >
        <Heading
          id="house-rules-title"
          level={headline.heading.level}
          className="xl:text-7xl lg:text-6xl sm:text-5xl text-4xl reveal-item tracking-tighter"
        >
          {headline.heading.title}
        </Heading>
        {headline.description && (
          <div className="max-w-xl text-black reveal-item">
            <BlocksRenderer content={headline.description} />
          </div>
        )}
      </div>

      <div
        className="reveal-item w-full max-w-screen-2xl mx-auto"
        ref={rootRef}
      >
        {isVisible ? (
          <div ref={sliderRef} className="keen-slider pb-14">
            {slides.map((room, idx) => {
              const href = `${roomHrefBase}/${room.alias}`;
              const imgUrl = pickBestImageUrl(room.image);
              const alt =
                room.image?.alternativeText ||
                room.image?.caption ||
                room.image?.name ||
                room.title;

              const location = room.hotel?.title || undefined;
              const logo = getHotelLogo(location);

              return (
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  key={room.id}
                  href={`https://dottingen.lmsgroup.ch${href}`}
                  className="keen-slider__slide"
                  aria-label={`Open room: ${room.title}`}
                >
                  <div className="flex flex-col justify-between p-6 xl:h-[800px] h-[600px] relative z-10">
                    {/* Overlay gradients */}
                    <div className="opacity-40 bg-gradient-to-t from-black to-transparent top-0 left-0 absolute w-full h-full -z-10" />
                    <div className="opacity-20 bg-gradient-to-b from-black to-transparent top-0 left-0 absolute w-full h-full -z-10" />

                    {/* Background image */}
                    {imgUrl ? (
                      <Image
                        className="object-cover object-center w-full h-full absolute top-0 left-0 -z-20"
                        width={573}
                        height={700}
                        alt={alt}
                        src={imgUrl}
                        loading="lazy"
                        sizes="(max-width: 768px) 90vw, (max-width: 1024px) 45vw, 33vw"
                      />
                    ) : (
                      <div
                        className="absolute top-0 left-0 w-full h-full -z-20 bg-neutral-200"
                        aria-hidden
                      />
                    )}

                    {/* Top content */}
                    <div className="text-white flex items-start gap-3">
                      {logo ? (
                        <Image
                          src={logo.src}
                          alt={`${location} logo`}
                          width={logo.width}
                          height={logo.height}
                          className={logo.className ?? "object-contain"}
                          sizes={
                            logo.sizes ??
                            "(max-width:768px) 120px, (max-width:1024px) 160px, 180px"
                          }
                          loading="lazy"
                        />
                      ) : (
                        location && (
                          <p className="md:text-3xl text-2xl">{location}</p>
                        )
                      )}
                    </div>

                    {/* Bottom content */}
                    <div className="text-white md:pr-34 pr-20 flex flex-col gap-1.5">
                      <h3 className="md:text-3xl text-2xl tracking-tighter">
                        {room.title}
                      </h3>
                      {room.shortDescription ? (
                        <p>{room.shortDescription}</p>
                      ) : Array.isArray(room.description) ? (
                        <div className="[&_*]:!my-0">
                          <BlocksRenderer content={room.description} />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pb-14 px-6">
            {slides.slice(0, 6).map((room) => {
              const imgUrl = pickBestImageUrl(room.image);
              const alt =
                room.image?.alternativeText ||
                room.image?.caption ||
                room.image?.name ||
                room.title;
              return (
                <div
                  key={room.id}
                  className="relative h-[320px] rounded-xl overflow-hidden"
                >
                  {imgUrl ? (
                    <Image
                      className="object-cover object-center w-full h-full"
                      width={600}
                      height={400}
                      alt={alt}
                      src={imgUrl}
                      loading="lazy"
                      sizes="(max-width: 768px) 90vw, (max-width: 1024px) 45vw, 33vw"
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
